import { createTheme, ThemeOptions } from '@mui/material/styles'
import { Theme } from '@mui/material/styles'

declare module '@mui/styles/defaultTheme' {
    interface DefaultTheme extends Theme {}
}
declare module '@mui/material/styles/createPalette' {
    interface Palette {
        quaternary: Palette['primary']
        tertiary: Palette['primary']
        disabled: Palette['primary']
        type: 'light' | 'dark'
    }
    interface PaletteOptions {
        quaternary?: Palette['primary']
        tertiary?: PaletteOptions['primary']
        disabled?: PaletteOptions['primary']
        type: 'light' | 'dark'
    }

    interface SimplePaletteColorOptions {
        gradient?: string
    }

    interface TypeText {
        hint?: string
        highlightSearchTerm?: string
    }
}

export const defaultThemeObject: ThemeOptions = {
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    palette: {
        common: {
            black: '#000',
            white: '#fff',
        },
        type: 'light',
        primary: {
            main: '#0057A0',
            light: 'rgba(48, 127, 193, 0.07)',
            dark: '#1f629a',
            gradient: 'linear-gradient(107deg,#682a94,#05afe0)',
        },
        secondary: {
            light: 'rgba(1, 174, 143, 0.07)',
            main: '#01ae8f',
            dark: '#118e77',
            gradient: 'linear-gradient(107deg, #019cae, #0057A0)',
            contrastText: '#fff',
        },
        tertiary: {
            light: 'rgba(69, 63, 187, 0.07)',
            main: '#453fbb',
            dark: '#1e1989',
            gradient: 'linear-gradient(107deg, #01ae8f, #0057A0)',
            contrastText: '#fff',
        },
        quaternary: {
            light: 'rgba(255, 60, 0, 0.07)',
            main: '#ff3c00',
            dark: '#bf2f03',
            contrastText: '#fff',
        },
        error: {
            light: 'rgba(255, 77, 95, 0.07)',
            main: '#ff4d5f',
            dark: '#ba002c',
            contrastText: '#fff',
        },
        warning: {
            light: 'rgba(255, 126, 0, 0.07)',
            main: '#ff7e00',
            dark: '#b86d24',
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        info: {
            light: 'rgba(38, 95, 180, 0.07)',
            main: '#265fb4',
            dark: '#0f3d81',
            contrastText: '#fff',
        },
        success: {
            light: 'rgba(62, 170, 5, 0.07)',
            main: '#3eaa05',
            dark: '#3c8416',
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        disabled: {
            light: 'rgba(208, 217, 226, 0.25)',
            main: '#d0d9e2',
            dark: '#aeb8c3',
        },
        grey: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#eeeeee',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#8b909a',
            A100: '#d5d5d5',
            A200: '#aaaaaa',
            A400: '#303030',
            A700: '#616161',
        },
        contrastThreshold: 3,
        tonalOffset: 0.2,
        text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.54)',
            disabled: 'rgba(0, 0, 0, 0.38)',
            hint: 'rgba(0, 0, 0, 0.38)',
            highlightSearchTerm: '#d36dbf',
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        background: {
            paper: '#ffffff',
            default: '#eef0f4',
        },
        action: {
            active: 'rgba(0, 0, 0, 0.54)',
            hover: 'rgba(0, 0, 0, 0.04)',
            hoverOpacity: 0.04,
            selected: 'rgba(0, 0, 0, 0.08)',
            selectedOpacity: 0.08,
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)',
            disabledOpacity: 0.38,
            focus: 'rgba(0, 0, 0, 0.12)',
            focusOpacity: 0.12,
            activatedOpacity: 0.12,
        },
    },
    typography: {
        htmlFontSize: 10,
        fontSize: 14,
        fontFamily: [
            'Rubik',
            'sans-serif',
            'Source Sans Pro',
            'Muli',
            'Roboto',
            'Helvetica',
            'Arial',
        ].join(','),
        h1: {
            fontSize: '6rem',
        },
        h2: {
            fontSize: '1.8rem',
        },
        h3: {
            fontSize: '4.8rem',
        },
        h4: {
            fontSize: '3.4rem',
        },
        h5: {
            fontSize: '2.4rem',
        },
        h6: {
            fontSize: '1.8rem',
        },
        subtitle1: {
            fontSize: '1rem',
            color: '#b4b4b4',
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '1rem',
            color: '#b4b4b4',
            fontWeight: 500,
        },
        body1: {
            fontSize: '1.6rem',
            fontWeight: 500,
        },
        body2: { fontSize: '1.4rem' },
        button: {
            fontSize: '1.4rem',
        },
        caption: {
            fontSize: '1.2rem',
        },
        overline: {
            fontSize: '1.2rem',
        },
    },
    shape: {
        borderRadius: 4,
    },
    components: {
        MuiTextField: {
            defaultProps: {
                variant: 'standard',
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '1.3rem',
                    color: '#ffffff',
                    backgroundColor: '#0057A0',
                },
            },
        },
        MuiListItemText: {
            defaultProps: {
                style: {
                    marginTop: '0.4rem',
                    marginBottom: '0.4rem',
                },
            },
        },
        MuiTable: {
            defaultProps: {
                style: {
                    boxShadow: '0 0.5rem 0.8rem 0 rgba(0, 0, 0, 0.05)',

                    border: 'solid 0.1rem #efeff1',
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                style: {
                    boxShadow: '0 0.5rem 0.8rem 0 rgba(0, 0, 0, 0.05)',
                    // border: 'solid 1px #efeff1', // be specific to wherever you want to apply this
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiAppBar: {
            defaultProps: {
                style: {
                    boxShadow: '0 0.1rem 0.2rem 0 rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiCard: {
            defaultProps: {
                style: {
                    // boxShadow: '0 5px 8px 0 rgba(0, 0, 0, 0.05)',
                    boxShadow: '0 0.1rem 0.2rem 0 rgba(0, 0, 0, 0.1)',
                    borderRadius: '0.5rem',
                    // border: 'solid 1px #ebebed',
                    border: 'none',
                },
            },
        },
        MuiInputBase: {
            defaultProps: {
                style: {
                    fontSize: '1.2rem',
                },
            },
        },
        MuiFormControl: {
            defaultProps: {
                style: {
                    marginTop: '0.8rem',
                },
            },
        },
        MuiInputLabel: {
            defaultProps: {
                style: {
                    fontSize: '1.2rem',
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                color: 'primary',
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: 'primary',
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    minHeight: '1em',
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                a: {
                    color: '#0057A0',
                    transition:
                        'color 150ms ease-in-out, text-decoration-color 150ms ease-in-out, text-decoration-offset 150ms ease-in-out',
                    textDecorationColor: 'rgba(48 127 193 / 0.4)',
                    textUnderlineOffset: '0.2rem',

                    ':hover, :active, :focus': {
                        textDecorationColor: 'currentColor',
                        textUnderlineOffset: '0.3rem',
                    },
                },
            },
        },
    },
    mixins: {
        toolbar: {
            minHeight: 111,
            '@media (min-width:0px) and (orientation: landscape)': {
                minHeight: 111,
            },
            '@media (min-width:960px)': {
                minHeight: 70,
            },
        },
        // @ts-ignore
        navBar: {
            // Custom mixin
            minHeight: 65,
            '@media (min-width:0px) and (orientation: landscape)': {
                minHeight: 65,
            },
            'media (min-width:600px)': {
                minHeight: 65,
            },
        },
        header: {
            // Custom mixin
            minHeight: 111,
            '@media (min-width:0px) and (orientation: landscape)': {
                minHeight: 111,
            },
            '@media (min-width:960px)': {
                minHeight: 70,
            },
        },
        headerTop: {
            // Custom mixin
            top: 111,
            '@media (min-width:0px) and (orientation: landscape)': {
                top: 111,
            },
            '@media (min-width:960px)': {
                top: 70,
            },
        },
        cartVisibleIconTop: {
            // Custom mixin
            top: -70,
        },
    },
}

export default createTheme(defaultThemeObject)
